import React, { useState } from "react";
import { Card, Row, Col, InputNumber, Button, Typography, Collapse, DatePicker } from "antd";
import { CalculatorOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;
const { Panel } = Collapse;

const ManualInputForm = ({ onCalculate, loading, onIVChange }) => {
  const [stockPrice, setStockPrice] = useState(75);
  const [iv, setIv] = useState(50);
  const [riskFreeRate, setRiskFreeRate] = useState(4.5);
  const [numExpirations, setNumExpirations] = useState(3);
  const [strikesAbove, setStrikesAbove] = useState(10);
  const [strikesBelow, setStrikesBelow] = useState(5);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const handleIVChange = (newIV) => {
    setIv(newIV);
    if (onIVChange) {
      onIVChange(newIV);
    }
  };

  const handleCalculate = () => {
    onCalculate({
      stockPrice,
      iv,
      riskFreeRate,
      numExpirations,
      strikesAbove,
      strikesBelow,
      customDate: selectedDate,
    });
  };

  return (
    <Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Text strong>Reference Date (Today):</Text>
          <DatePicker
            style={{ width: "100%", marginTop: 8 }}
            value={selectedDate}
            onChange={setSelectedDate}
            disabled={loading}
            format="YYYY-MM-DD"
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Text strong>Stock Price ($):</Text>
          <InputNumber
            style={{ width: "100%", marginTop: 8 }}
            min={0.01}
            max={10000}
            step={0.01}
            value={stockPrice}
            onChange={setStockPrice}
            disabled={loading}
            precision={2}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Text strong>Implied Volatility (%):</Text>
          <InputNumber
            style={{ width: "100%", marginTop: 8 }}
            min={1}
            max={200}
            step={1}
            value={iv}
            onChange={handleIVChange}
            disabled={loading}
            formatter={(value) => `${value}%`}
            parser={(value) => value.replace("%", "")}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ height: "22px" }} /> {/* Spacer for alignment */}
          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            onClick={handleCalculate}
            loading={loading}
            block
            style={{ marginTop: 8 }}
          >
            Calculate Options
          </Button>
        </Col>
      </Row>

      <Collapse
        ghost
        style={{ marginTop: 16 }}
        items={[
          {
            key: "advanced",
            label: (
              <Text type="secondary">
                <SettingOutlined /> Advanced Settings
              </Text>
            ),
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Text strong>Risk-Free Rate (%):</Text>
                  <InputNumber
                    style={{ width: "100%", marginTop: 8 }}
                    min={0}
                    max={20}
                    step={0.1}
                    value={riskFreeRate}
                    onChange={setRiskFreeRate}
                    disabled={loading}
                    precision={1}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace("%", "")}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Default: 4.5%
                  </Text>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Text strong>Expirations:</Text>
                  <InputNumber
                    style={{ width: "100%", marginTop: 8 }}
                    min={1}
                    max={10}
                    value={numExpirations}
                    onChange={setNumExpirations}
                    disabled={loading}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Number of Friday expirations
                  </Text>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Text strong>Strikes Above:</Text>
                  <InputNumber
                    style={{ width: "100%", marginTop: 8 }}
                    min={1}
                    max={50}
                    value={strikesAbove}
                    onChange={setStrikesAbove}
                    disabled={loading}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Strikes above current price
                  </Text>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Text strong>Strikes Below:</Text>
                  <InputNumber
                    style={{ width: "100%", marginTop: 8 }}
                    min={1}
                    max={50}
                    value={strikesBelow}
                    onChange={setStrikesBelow}
                    disabled={loading}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Strikes below current price
                  </Text>
                </Col>
              </Row>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default ManualInputForm;
